import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { PlanningSaveStatus } from '@/components/planning/PlanningSaveStatus';
import { SessionLostBanner } from '@/components/planning/SessionLostBanner';
import { PlanningConflictsPanel } from '@/components/planning/PlanningConflictsPanel';
import {
  applyDraft, cellKey, sameAsServer, clearDraft, daysToSchedules, describeSaveError, loadDraft, mergeServerIntoLocal,
  normalizeServerRow, saveDraft, touchedCells,
} from '@/components/planning/planningSync';
import { InternalPlanningSection } from '@/components/internal-staff/InternalPlanningSection';
import { canManageInternal } from '@/components/internal-staff/shared';
import { notifyEmployeeActivityChange } from '@/utils/employeeActivity';
import { Calendar, ChevronLeft, ChevronRight, Clock, Save, Users, Coffee, Check, X, AlertTriangle, ClipboardCheck, MessageSquare, Mail, Send, UserX, UserCheck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
const AdminPlanning = ({ user, activeModule = "b2b" }) => {
  function parseLocalDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
  }

  function formatLocalDate(dateObj) {
    return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
  }

  function getWeekEndFromStart(weekStartDate) {
    const date = parseLocalDate(weekStartDate);
    date.setDate(date.getDate() + 6);
    return formatLocalDate(date);
  }

  function isCountableSchedule(schedule) {
    if (!schedule) return false;
    if (schedule.validation_status === 'rejected') return false;
    const status = schedule.status || 'repos';
    if (status === 'prevu') return true;
    if (status === 'present') return (schedule.validation_status || '').toLowerCase() === 'approved';
    return false;
  }

  function getValidationAwareStatus(schedule, fallbackStatus = 'repos') {
    const status = schedule?.status || fallbackStatus;
    const validation = (schedule?.validation_status || '').toLowerCase();

    if (status === 'present') {
      if (validation === 'approved') return 'present';
      if (validation === 'rejected') return 'refus_validation';
      return 'en_attente_validation';
    }

    return status;
  }

  const storedPeriod = (() => {
    try { return JSON.parse(localStorage.getItem(`dlv_admin_planning_period:${user?.user_id || 'anon'}`) || 'null'); } catch (e) { return null; }
  })();
  const [viewMode, setViewMode] = useState(storedPeriod?.viewMode || 'week'); // 'week' or 'month'
  const [weekStart, setWeekStart] = useState(() => {
    if (storedPeriod?.weekStart) return storedPeriod.weekStart;
    const today = new Date();
    const day = today.getDay();
    // Calculate Monday: if Sunday (0), go back 6 days; otherwise go back to Monday
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    return formatLocalDate(monday);
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (storedPeriod?.currentMonth) return storedPeriod.currentMonth;
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [monthlySchedules, setMonthlySchedules] = useState({});
  const [missingPhotos, setMissingPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [saveState, setSaveState] = useState({ kind: 'idle' });
  const [draftRestored, setDraftRestored] = useState(null);
  const hasLoadedOnce = useRef(false);
  const draftChecked = useRef(false);
  const fetchSeq = useRef(0);
  const saveRequestRef = useRef(null);
  const fetchDataRef = useRef(null);
  const savingRef = useRef(false);
  const inFlight = useRef(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [newSiteName, setNewSiteName] = useState('');
  
  // Comment modal state
  const [commentModal, setCommentModal] = useState({ open: false, userId: null, date: null, comment: '' });
  const [savingComment, setSavingComment] = useState(false);
  
  // Email notification modal state
  const [emailModal, setEmailModal] = useState({ open: false });
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [expandedList, setExpandedList] = useState(null); // 'sent', 'failed', 'no_schedule'
  const [weekPublication, setWeekPublication] = useState({ status: 'draft', published_at: null });
  const [publishingWeek, setPublishingWeek] = useState(false);
  const [archiveModal, setArchiveModal] = useState({ open: false, loading: false, records: [] });
  const [applyingWeekendDefaults, setApplyingWeekendDefaults] = useState(false);
  const isSuperAdmin = user?.is_super_admin === true || user?.email === 'transport@dlv-express.com';
  const hasFullPlanningAccess = isSuperAdmin || user?.admin_level === 'full';
  const isLimitedPlanningAdmin = user?.role === 'admin' && !hasFullPlanningAccess;
  const limitedPlanningAllowedStatuses = ['prevu', 'absent', 'repos', 'repos_force'];
  
  // Deactivation modal state
  const [deactivateModal, setDeactivateModal] = useState({ open: false, employee: null });
  const [deactivating, setDeactivating] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 640 : false));

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate days based on view mode
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = parseLocalDate(weekStart);
    date.setDate(date.getDate() + i);
    return formatLocalDate(date);
  });

  // Generate all days in the current month
  const getMonthDays = () => {
    const { year, month } = currentMonth;
    const lastDay = new Date(year, month + 1, 0); // Last day of the month
    const days = [];
    for (let d = 1; d <= lastDay.getDate(); d++) {
      // Format date manually to avoid timezone issues
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push(dateStr);
    }
    return days;
  };

  const displayDays = viewMode === 'month' ? getMonthDays() : weekDays;

  useEffect(() => {
    fetchData();
    try { localStorage.setItem(`dlv_admin_planning_period:${user?.user_id || 'anon'}`, JSON.stringify({ viewMode, weekStart, currentMonth })); } catch (e) { /* ignore */ }
  }, [weekStart, currentMonth, viewMode]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get(`${API}/schedule/integrity/duplicates`, { withCredentials: true })
      .then(r => setDuplicateCount(r.data?.count || 0)).catch(() => {});
  }, [isSuperAdmin]);

  const pendingCells = useMemo(() => touchedCells(schedules), [schedules]);
  const conflictCells = useMemo(() => pendingCells.filter(c => c.cell._conflict), [pendingCells]);
  const hasChanges = pendingCells.length > 0;
  const draftPending = pendingCells.some(c => c.cell._from_draft);
  useEffect(() => { if (draftRestored && !draftPending) setDraftRestored(null); }, [draftRestored, draftPending]);

  const [sessionLost, setSessionLost] = useState(false);
  const schedulesRef = useRef(schedules);
  schedulesRef.current = schedules;
  useEffect(() => {
    const onLost = () => { saveDraft(user?.user_id, schedulesRef.current); setSessionLost(true); };
    window.addEventListener('dlv:session-lost', onLost);
    return () => window.removeEventListener('dlv:session-lost', onLost);
  }, [user?.user_id]);

  useEffect(() => {
    if (!draftChecked.current) return;
    const t = setTimeout(() => saveDraft(user?.user_id, schedules), 300);
    return () => clearTimeout(t);
  }, [schedules, user?.user_id]);

  useEffect(() => {
    if (!hasChanges) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasChanges]);

  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'visible' || savingRef.current || !hasLoadedOnce.current) return;
      fetchDataRef.current?.({ silent: true });
    };
    const id = setInterval(sync, 45000);
    document.addEventListener('visibilitychange', sync);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', sync); };
  }, []);

  useEffect(() => {
    if (viewMode === 'week') {
      fetchWeekPublicationStatus();
      applyWeekendDefaults(false);
    }
  }, [weekStart, viewMode]);

  const periodBounds = () => {
    const days = viewMode === 'month' ? getMonthDays() : weekDays;
    const monthDate = viewMode === 'month'
      ? new Date(currentMonth.year, currentMonth.month, 1)
      : parseLocalDate(weekStart);
    const startOfMonth = formatLocalDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1, 12, 0, 0));
    const endOfMonth = formatLocalDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 12, 0, 0));
    return { days, startOfMonth, endOfMonth };
  };

  // silent = actualisation sans vider la grille (position, filtres et saisies conservés)
  const fetchData = async ({ silent = false, force = false } = {}) => {
      if (silent && !force && inFlight.current > 0) return;
      inFlight.current += 1;
      const requestId = ++fetchSeq.current;
      const firstLoad = !hasLoadedOnce.current;
      if (firstLoad) setLoading(true); else if (silent) setRefreshing(true); else setPeriodLoading(true);
      try {
        const { days, startOfMonth, endOfMonth } = periodBounds();
        const rangeStart = days[0] < startOfMonth ? days[0] : startOfMonth;
        const rangeEnd = days[days.length - 1] > endOfMonth ? days[days.length - 1] : endOfMonth;
        const [empRes, sitesRes, rangeRes] = await Promise.all([
          axios.get(`${API}/users`, { withCredentials: true }),
          axios.get(`${API}/sites`, { withCredentials: true }),
          axios.get(`${API}/schedule/range?start_date=${rangeStart}&end_date=${rangeEnd}`, { withCredentials: true }),
        ]);
        if (requestId !== fetchSeq.current) return;
        const activeEmployees = empRes.data.filter(e => {
          const isEmployee = e.role === 'employee';
          const isActive = e.active !== false;
          const hasValidStatus = e.status === 'approved' || e.status === 'pending';
          return isSuperAdmin ? isEmployee && isActive : isEmployee && isActive && hasValidStatus;
        });
        activeEmployees.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const allDays = rangeRes.data?.days || {};
        const visibleDays = {};
        days.forEach(d => { visibleDays[d] = allDays[d] || []; });
        const serverSchedules = daysToSchedules(visibleDays);
        const monthlyData = {};
        Object.entries(allDays).forEach(([d, rows]) => {
          if (d < startOfMonth || d > endOfMonth) return;
          rows.forEach(r => {
            if (!r.schedule_id) return;
            (monthlyData[r.user_id] = monthlyData[r.user_id] || []).push({ ...r, status: r.raw_status || r.status });
          });
        });
        setSites(sitesRes.data);
        setEmployees(activeEmployees);
        setSchedules(prev => {
          let merged = mergeServerIntoLocal(prev, serverSchedules);
          if (!draftChecked.current) {
            draftChecked.current = true;
            const loaded = loadDraft(user?.user_id);
            const draft = loaded && { ...loaded, cells: (loaded.cells || []).filter(({ date, userId, cell }) => !sameAsServer(cell, serverSchedules[date]?.[userId])) };
            if (draft?.cells?.length) {
              merged = mergeServerIntoLocal(applyDraft(prev, draft), serverSchedules);
              setDraftRestored({ count: draft.cells.length, saved_at: draft.saved_at });
            }
          }
          return merged;
        });
        setMonthlySchedules(monthlyData);
        setLoadError(false);
        setLastSync(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        hasLoadedOnce.current = true;
        if (!silent) {
          const today = formatLocalDate(new Date());
          axios.get(`${API}/photos/admin/missing?date=${today}`, { withCredentials: true })
            .then(r => setMissingPhotos(r.data)).catch(() => {});
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        if (!hasLoadedOnce.current) setLoadError(true);
        else if (!silent) toast.error("Actualisation impossible : le planning affiché n'est peut-être pas à jour.");
      } finally {
        inFlight.current -= 1;
        if (requestId === fetchSeq.current) {
          setLoading(false);
          setRefreshing(false);
          setPeriodLoading(false);
        }
      }
  };

  fetchDataRef.current = fetchData;

  const fetchWeekPublicationStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/schedule/week-publication-status?week_start=${weekStart}`,
        { withCredentials: true }
      );
      setWeekPublication(response.data || { status: 'draft', published_at: null });
    } catch (error) {
      console.error('Failed to fetch week publication status:', error);
      setWeekPublication({ status: 'draft', published_at: null });
    }
  };

  // Function to get automatic loading time based on site and day of week
  const getAutoLoadingTime = (siteName, date) => {
    if (!siteName) return null;
    
    const siteNameLower = siteName.toLowerCase();
    
    // TOPC (DIF4 ou DIF6) → 10:30
    if (siteNameLower.includes('topc')) {
      return '10:30';
    }
    
    // IDF → 10:00
    if (siteNameLower.includes('idf')) {
      return '10:00';
    }
    
    // STSA DIF6 uniquement → 10:50
    if (siteNameLower.includes('stsa') && siteNameLower.includes('dif6')) {
      return '10:50';
    }
    
    // Colis Privé → 08:00 (tous les jours)
    if (siteNameLower.includes('colis') || siteNameLower.includes('privé') || siteNameLower.includes('prive')) {
      return '08:00';
    }
    
    // Autres sites (TAXS, STSA DIF4, etc.) → pas d'horaire automatique
    return null;
  };

  // Function to get automatic comment based on site
  const getAutoComment = (siteName) => {
    if (!siteName) return null;
    
    const siteNameLower = siteName.toLowerCase();
    
    // IDF, TOPC, ou STSA DIF6 → "Rdv Next"
    if (siteNameLower.includes('idf') || siteNameLower.includes('topc') || 
        (siteNameLower.includes('stsa') && siteNameLower.includes('dif6'))) {
      return 'Rdv Next';
    }
    
    return null; // No auto comment for other sites
  };

  const hasNextFallbackSite = (siteName) => getAutoComment(siteName) === 'Rdv Next';

  const isAmazonSiteName = (siteName = '') => {
    const normalized = siteName.toLowerCase();
    return !(normalized.includes('colis') || normalized.includes('privé') || normalized.includes('prive'));
  };

  const getAmazonAutoTimes = (tourType) => {
    const mapping = {
      'SDA': { display: 'SDA 06:45', starts: [{ label: 'SDA', time: '06:45' }] },
      'SDB': { display: 'SDB 13:00', starts: [{ label: 'SDB', time: '13:00' }] },
      'SDC': { display: 'SDC 18:00', starts: [{ label: 'SDC', time: '18:00' }] },
      'SDA+B': {
        display: 'SDA 06:45 · SDB 13:00',
        starts: [
          { label: 'SDA', time: '06:45' },
          { label: 'SDB', time: '13:00' }
        ]
      },
      'SDA+C': {
        display: 'SDA 06:45 · SDC 18:00',
        starts: [
          { label: 'SDA', time: '06:45' },
          { label: 'SDC', time: '18:00' }
        ]
      },
      'SDB+C': {
        display: 'SDB 13:00 · SDC 18:00',
        starts: [
          { label: 'SDB', time: '13:00' },
          { label: 'SDC', time: '18:00' }
        ]
      }
    };
    return mapping[tourType] || { display: '', starts: [] };
  };

  const applyWeekendDefaults = async (manual = false) => {
    if (viewMode !== 'week' || applyingWeekendDefaults) return;
    setApplyingWeekendDefaults(true);
    try {
      const response = await axios.post(
        `${API}/schedule/weekend-only/apply-defaults`,
        { week_start: weekStart },
        { withCredentials: true }
      );

      const created = response.data?.created_count || 0;
      const updatedLocked = response.data?.updated_locked_count || 0;
      if (manual || created > 0 || updatedLocked > 0) {
        await fetchData({ silent: true });
      }
    } catch (error) {
      console.error('Failed to apply weekend defaults:', error);
      if (manual) {
        alert(error.response?.data?.detail || 'Erreur lors de l\'application des règles week-end');
      }
    } finally {
      setApplyingWeekendDefaults(false);
    }
  };

  const updateSchedule = (date, userId, field, value) => {
    if (field === 'status' && isLimitedPlanningAdmin && !limitedPlanningAllowedStatuses.includes((value || '').toLowerCase())) {
      return;
    }

    const currentSchedule = schedules[date]?.[userId];
    const employee = employees.find(e => e.user_id === userId);
    const siteName = sites.find(s => s.site_id === employee?.site_id)?.name || '';
    const isAmazonEmployee = !!employee?.site_id && isAmazonSiteName(siteName);

    // Weekend-only contract rule: Monday to Thursday are locked (repos_demande)
    const dayOfWeek = parseLocalDate(date).getDay(); // 0=Sun,1=Mon,...
    const isWeekendOnlyEmployee = employee?.work_pattern === 'weekend_only';
    const isLockedWeekdayForWeekendOnly = isWeekendOnlyEmployee && isAmazonEmployee && dayOfWeek >= 1 && dayOfWeek <= 4;
    if (isLockedWeekdayForWeekendOnly) {
      return;
    }
    
    // Alert if trying to modify a day with approved day-off request
    if (currentSchedule?.status === 'repos_demande' && field === 'status' && value !== 'repos_demande') {
      const employeeName = employee?.name || 'Ce chauffeur';
      const formattedDate = new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
      
      const confirmChange = window.confirm(
        `⚠️ ATTENTION: ${employeeName} a une demande de repos approuvée pour le ${formattedDate}.\n\nLe chauffeur ne sera pas présent ce jour-là.\n\nVoulez-vous vraiment modifier ce statut ?`
      );
      
      if (!confirmChange) {
        return; // Cancel the change
      }
    }

    // Repos forcé : ne jamais écraser silencieusement une journée déjà renseignée
    if (field === 'status' && value === 'repos_force' && currentSchedule) {
      const filledParts = [];
      if (['prevu', 'present'].includes(currentSchedule.status)) filledParts.push(`statut « ${getStatusLabel(currentSchedule.status)} »`);
      if (currentSchedule.tour_type) filledParts.push(`prestation « ${currentSchedule.tour_type} »`);
      if (currentSchedule.loading_time || currentSchedule.start_time) filledParts.push(`horaire ${currentSchedule.start_time || currentSchedule.loading_time}`);
      if (currentSchedule.end_time) filledParts.push(`fin de service ${currentSchedule.end_time}`);
      if (currentSchedule.validation_status) filledParts.push(`validation « ${currentSchedule.validation_status} »`);
      if (filledParts.length > 0) {
        const employeeName = employee?.name || 'Ce chauffeur';
        const formattedDate = parseLocalDate(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
        const confirmForce = window.confirm(
          `⚠️ CONFLIT : la journée du ${formattedDate} de ${employeeName} contient déjà :\n• ${filledParts.join('\n• ')}\n\nPasser en « Repos forcé » retirera cette journée des heures travaillées (aucune donnée n'est supprimée, l'historique est conservé).\n\nConfirmer le passage en Repos forcé ?`
        );
        if (!confirmForce) {
          return;
        }
      }
    }
    
    // Auto-fill loading_time and comment when status changes to 'prevu'
    let autoLoadingTime = null;
    let autoComment = null;
    if (field === 'status' && value === 'prevu') {
      // Get employee's site name
      const siteName = sites.find(s => s.site_id === employee?.site_id)?.name;
      autoLoadingTime = getAutoLoadingTime(siteName, date);
      autoComment = getAutoComment(siteName);
      // NEXT is fallback-only, do not persist as concurrent comment
      if (autoComment === 'Rdv Next') {
        autoComment = null;
      }
    }
    
    setSchedules(prev => {
      const currentLoadingTime = prev[date]?.[userId]?.loading_time;
      const currentComment = prev[date]?.[userId]?.comment;
      const isIntegrationStatusUpdate = field === 'status' && value === 'en_cours_integration';
      
      // Only set auto values if:
      // 1. We're changing status to 'prevu'
      // 2. There's an auto value available
      // 3. Current value is empty (don't override manual input)
      const newLoadingTime = isIntegrationStatusUpdate
        ? null
        : (field === 'status' && value === 'prevu' && autoLoadingTime && !currentLoadingTime)
        ? autoLoadingTime
        : (field === 'loading_time' ? value : prev[date]?.[userId]?.loading_time);
      
      const hasRealPrestationSelected = field === 'tour_type' && value && value !== 'NEXT';
      const shouldClearLegacyRdvNext = hasRealPrestationSelected && (currentComment === 'Rdv Next' || currentComment?.includes('Rdv'));

      const newComment = isIntegrationStatusUpdate
        ? null
        : shouldClearLegacyRdvNext
        ? null
        : (field === 'status' && value === 'prevu' && autoComment && !currentComment)
        ? autoComment
        : (field === 'comment' ? value : prev[date]?.[userId]?.comment);

      const autoTimes = field === 'tour_type' ? getAmazonAutoTimes(value) : null;
      const siteNameForTime = sites.find(s => s.site_id === employee?.site_id)?.name;
      const fallbackNextTime = getAutoLoadingTime(siteNameForTime, date);
      const existingStartTime = prev[date]?.[userId]?.start_time;
      const existingSecondStartTime = prev[date]?.[userId]?.second_start_time;
      const existingEndTime = prev[date]?.[userId]?.end_time;
      const primaryAutoTime = autoTimes?.starts?.[0]?.time || null;
      const secondaryAutoTime = autoTimes?.starts?.[1]?.time || null;
      const autoStartForTour = field === 'tour_type'
        ? (
          value === 'NEXT'
            ? (fallbackNextTime || null)
            : (
              value === "Assistance chef d'équipe" || value === 'Backup'
                ? (fallbackNextTime || null)
                : (primaryAutoTime || null)
            )
        )
        : null;
      const newStartTime = isIntegrationStatusUpdate
        ? null
        : field === 'tour_type'
        ? (existingStartTime || autoStartForTour)
        : (field === 'start_time' ? value : existingStartTime);
      const newSecondStartTime = isIntegrationStatusUpdate
        ? null
        : field === 'tour_type'
        ? (existingSecondStartTime || secondaryAutoTime || null)
        : (field === 'second_start_time' ? value : existingSecondStartTime);
      const newEndTime = isIntegrationStatusUpdate
        ? null
        : field === 'tour_type'
        ? existingEndTime
        : (field === 'end_time' ? value : existingEndTime);
      const nextTourType = isIntegrationStatusUpdate
        ? null
        : (field === 'tour_type' ? value : prev[date]?.[userId]?.tour_type);
      const nextValidationStatus = isIntegrationStatusUpdate
        ? null
        : prev[date]?.[userId]?.validation_status;

      return {
        ...prev,
        [date]: {
          ...prev[date],
          [userId]: {
            ...prev[date]?.[userId],
            user_id: userId,
            [field]: value,
            _touched: true,
            _explicit_repos: field === 'status' ? (value === 'repos' || value === 'repos_force') : prev[date]?.[userId]?._explicit_repos === true,
            tour_type: nextTourType,
            loading_time: newLoadingTime,
            comment: newComment,
            start_time: newStartTime,
            second_start_time: newSecondStartTime,
            end_time: newEndTime,
            validation_status: nextValidationStatus
          }
        }
      };
    });
    setSaveState(st => (st.kind === 'saving' ? st : { kind: 'dirty' }));
  };

  const buildPayload = (date, userId, schedule) => {
    const employee = employees.find(e => e.user_id === userId);
    const siteName = sites.find(s => s.site_id === employee?.site_id)?.name || '';
    const isAmazonEmployee = !!employee?.site_id && isAmazonSiteName(siteName);
    const payload = {
      user_id: userId,
      date,
      status: schedule.status || 'repos',
      loading_time: schedule.loading_time || null,
      comment: schedule.comment || null,
      admin_comment: schedule.admin_comment || null,
      baseline_updated_at: schedule._baseline_updated_at || null,
      explicit_repos: schedule._explicit_repos === true
    };
    if (isAmazonEmployee && schedule.status === 'prevu') {
      payload.tour_type = schedule.tour_type || null;
      payload.start_time = schedule.start_time || null;
      payload.second_start_time = schedule.second_start_time || null;
      payload.end_time = null;
    }
    if (schedule.status === 'en_cours_integration') {
      Object.assign(payload, { loading_time: null, comment: null, tour_type: null, start_time: null, second_start_time: null, end_time: null });
    }
    return payload;
  };

  const postBulk = async (body) => {
    try {
      return (await axios.post(`${API}/schedule/bulk`, body, { withCredentials: true, timeout: 30000 })).data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status !== 409 || detail?.code !== 'mass_repos_confirmation_required') throw error;
      const names = (detail.downgrades || []).slice(0, 10).map(d => `• ${d.user_name || d.user_id} — ${d.date} (${d.old_status} → ${d.new_status === 'repos_force' ? 'Repos forcé' : 'Repos'})`).join('\n');
      const confirmed = window.confirm(
        `⚠️ CONFIRMATION REQUISE\n\n${detail.count} journée(s) actuellement actives (mission/prévu/présent) passeraient en REPOS / REPOS FORCÉ :\n\n${names}${detail.count > 10 ? '\n…' : ''}\n\nConfirmez-vous vouloir réellement modifier ces journées ?`
      );
      if (!confirmed) return null;
      return (await axios.post(`${API}/schedule/bulk`, { ...body, confirm_mass_repos: true, client_request_id: `${body.client_request_id}-c` }, { withCredentials: true, timeout: 30000 })).data;
    }
  };

  const saveAllSchedules = async () => {
    if (savingRef.current) return;
    if (loading || loadError) {
      alert("Le planning ne s'est pas chargé correctement. Veuillez recharger la page avant d'enregistrer.");
      return;
    }
    const cells = touchedCells(schedules).filter(c => !c.cell._conflict);
    if (cells.length === 0) {
      toast.info(conflictCells.length ? 'Arbitrez d’abord les conflits signalés.' : 'Aucune modification à enregistrer.');
      return;
    }
    const allSchedules = cells.map(({ date, userId, cell }) => buildPayload(date, userId, cell));
    const signature = JSON.stringify(allSchedules);
    // même lot renvoyé après une coupure = même identifiant : le serveur ne l'applique jamais deux fois
    if (saveRequestRef.current?.signature !== signature) {
      saveRequestRef.current = { signature, id: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}` };
    }
    savingRef.current = true;
    fetchSeq.current += 1; // invalide toute synchro en cours : sa réponse serait antérieure à l'enregistrement
    setSaving(true);
    setSaveState({ kind: 'saving' });
    try {
      const result = await postBulk({ schedules: allSchedules, client_request_id: saveRequestRef.current.id });
      if (!result) { saveRequestRef.current = null; setSaveState({ kind: 'dirty' }); return; }
      saveRequestRef.current = null;
      const applied = new Set(result.applied_keys || []);
      const failed = new Set((result.verification_failures || []).map(f => cellKey(f.user_id, f.date)));
      const conflicts = new Map((result.conflict_skipped_entries || []).map(c => [cellKey(c.user_id, c.date), c.server_row]));
      const rejected = new Set([...(result.protected_skipped_entries || []), ...(result.locked_weekday_entries || []),
        ...(result.inactive_employee_blocked_entries || []), ...(result.out_of_scope_entries || [])].map(r => cellKey(r.user_id, r.date)));
      const sent = new Map(cells.map(c => [cellKey(c.userId, c.date), JSON.stringify(c.cell)]));
      setSchedules(prev => {
        const next = { ...prev };
        Object.entries(prev).forEach(([date, day]) => {
          Object.entries(day || {}).forEach(([userId, cell]) => {
            const key = cellKey(userId, date);
            if (!sent.has(key) || sent.get(key) !== JSON.stringify(cell)) return;
            if (applied.has(key) && !failed.has(key)) {
              const { _touched, _from_draft, _conflict, _server, ...clean } = cell;
              next[date] = { ...next[date], [userId]: { ...clean, _baseline_updated_at: result.server_timestamp, updated_at: result.server_timestamp } };
            } else if (rejected.has(key)) {
              const { _touched, _from_draft, ...rest } = cell;
              next[date] = { ...next[date], [userId]: rest };
            } else if (conflicts.has(key)) {
              next[date] = { ...next[date], [userId]: { ...cell, _conflict: true, _server: conflicts.get(key) } };
            }
          });
        });
        saveDraft(user?.user_id, next); // brouillon purgé immédiatement : pas de faux « non enregistré » si on recharge aussitôt
        return next;
      });
      const notes = [];
      if (result.conflict_skipped > 0) notes.push(`${result.conflict_skipped} conflit(s) avec un autre manager, à arbitrer`);
      if (result.protected_skipped > 0) notes.push(`${result.protected_skipped} passage(s) en repos refusé(s) (journée déjà renseignée)`);
      if (result.locked_weekday_skipped > 0) notes.push(`${result.locked_weekday_skipped} jour(s) verrouillé(s) contrat week-end non modifiés`);
      if (result.inactive_employee_skipped > 0) notes.push(`${result.inactive_employee_skipped} salarié(s) inactif(s) non modifiés`);
      if (result.out_of_scope_skipped > 0) notes.push(`${result.out_of_scope_skipped} salarié(s) hors de votre périmètre non modifiés`);
      if (failed.size > 0) notes.push(`${failed.size} cellule(s) non confirmée(s) à la relecture`);
      const at = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      setSaveState({ kind: 'saved', at, message: `${result.verified_count || 0} cellule(s) enregistrée(s)${notes.length ? ` · ${notes.join(' · ')}` : ''}` });
      if (notes.length) toast.warning(notes.join('\n'), { duration: 10000 });
      else toast.success(`${result.verified_count || 0} cellule(s) enregistrée(s) et confirmée(s)`);
      await fetchData({ silent: true, force: true });
    } catch (error) {
      console.error('Failed to save:', error);
      const message = describeSaveError(error);
      // refus explicite du serveur (droits, validation) : rien n'a été appliqué, le prochain essai est un nouveau lot
      if (error.response && error.response.data?.detail?.code !== 'save_in_progress') saveRequestRef.current = null;
      setSaveState({ kind: 'error', message });
      if (error.response?.status !== 401) toast.error(message, { duration: 12000 });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const resolveConflict = (item, keepMine) => {
    setSchedules(prev => {
      const server = item.cell._server || {};
      const { _conflict, _server, ...mine } = item.cell;
      const nextCell = keepMine
        ? { ...mine, _baseline_updated_at: server.updated_at || null }
        : normalizeServerRow({ ...(prev[item.date]?.[item.userId] || {}), ...server, user_id: item.userId });
      if (!keepMine) { delete nextCell._touched; delete nextCell._from_draft; delete nextCell._conflict; delete nextCell._server; }
      return { ...prev, [item.date]: { ...prev[item.date], [item.userId]: nextCell } };
    });
  };

  const discardChanges = () => {
    if (!window.confirm(`Abandonner ${pendingCells.length} modification(s) non enregistrée(s) ?`)) return;
    setSchedules(prev => {
      const next = { ...prev };
      pendingCells.forEach(({ date, userId, cell }) => {
        const { _touched, _from_draft, _conflict, _server, ...rest } = cell;
        next[date] = { ...next[date], [userId]: rest };
      });
      return next;
    });
    clearDraft(user?.user_id);
    setDraftRestored(null);
    setSaveState({ kind: 'idle' });
    fetchData({ silent: true }).then(() => {});
  };

  const handleAssignSite = async (userId, siteId) => {
    try {
      await axios.put(`${API}/users/${userId}/site`, { site_id: siteId }, { withCredentials: true });
      toast.success('Site enregistré');
      fetchData({ silent: true });
    } catch (error) {
      console.error('Failed to assign site:', error);
      toast.error(describeSaveError(error));
    }
  };

  // Open comment modal
  const openCommentModal = (userId, date) => {
    const currentComment = schedules[date]?.[userId]?.admin_comment || '';
    setCommentModal({ open: true, userId, date, comment: currentComment });
  };

  // Close comment modal
  const closeCommentModal = () => {
    setCommentModal({ open: false, userId: null, date: null, comment: '' });
  };

  // Save admin comment
  const saveAdminComment = async () => {
    setSavingComment(true);
    try {
      const commentRes = await axios.put(
        `${API}/schedule/${commentModal.userId}/${commentModal.date}/admin-comment`,
        { admin_comment: commentModal.comment },
        { withCredentials: true }
      );
      const commentTs = commentRes.data?.updated_at || null;
      
      // Update local state
      setSchedules(prev => ({
        ...prev,
        [commentModal.date]: {
          ...prev[commentModal.date],
          [commentModal.userId]: {
            ...prev[commentModal.date]?.[commentModal.userId],
            admin_comment: commentModal.comment,
            ...(commentTs ? { updated_at: commentTs, _baseline_updated_at: commentTs } : {})
          }
        }
      }));
      
      closeCommentModal();
    } catch (error) {
      console.error('Failed to save comment:', error);
      toast.error(`Commentaire non enregistré. ${describeSaveError(error)}`);
    } finally {
      setSavingComment(false);
    }
  };

  // Send planning notifications to employees
  const sendPlanningNotifications = async () => {
    setSendingEmails(true);
    setEmailResult(null);
    
    try {
      // Calculate week dates based on current view
      let weekStartDate, weekEndDate;
      
      if (viewMode === 'week') {
        const startDate = parseLocalDate(weekStart);
        weekStartDate = formatLocalDate(startDate);
        weekEndDate = getWeekEndFromStart(weekStartDate);
      } else {
        // For month view, use the current week's Monday
        const today = new Date();
        const day = today.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        weekStartDate = formatLocalDate(monday);
        weekEndDate = formatLocalDate(sunday);
      }
      
      const response = await axios.post(
        `${API}/schedule/send-planning-notifications`,
        { week_start: weekStartDate, week_end: weekEndDate },
        { withCredentials: true }
      );
      
      setEmailResult(response.data);
      if (response.data?.week_publication) {
        setWeekPublication(response.data.week_publication);
      }
    } catch (error) {
      console.error('Failed to send notifications:', error);
      setEmailResult({ 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'envoi des notifications' 
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const publishCurrentWeek = async () => {
    if (publishingWeek || viewMode !== 'week') return;
    setPublishingWeek(true);
    try {
      const weekEnd = getWeekEndFromStart(weekStart);
      const response = await axios.post(
        `${API}/schedule/week/publish`,
        { week_start: weekStart, week_end: weekEnd },
        { withCredentials: true }
      );
      if (response.data?.week_publication) {
        setWeekPublication(response.data.week_publication);
      } else {
        await fetchWeekPublicationStatus();
      }
    } catch (error) {
      console.error('Failed to publish planning week:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la publication du planning');
    } finally {
      setPublishingWeek(false);
    }
  };

  const openArchiveModal = async () => {
    setArchiveModal({ open: true, loading: true, records: [] });
    try {
      const response = await axios.get(
        `${API}/schedule/published-archive?week_start=${weekStart}&latest_only=true`,
        { withCredentials: true }
      );
      setArchiveModal({ open: true, loading: false, records: response.data?.records || [] });
    } catch (error) {
      console.error('Failed to fetch published archive:', error);
      setArchiveModal({ open: true, loading: false, records: [] });
      alert(error.response?.data?.detail || 'Erreur lors du chargement de l\'archive');
    }
  };

  const changeWeek = (direction) => {
    const date = parseLocalDate(weekStart);
    date.setDate(date.getDate() + (direction * 7));
    setWeekStart(formatLocalDate(date));
  };

  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = prev.month + direction;
      if (newMonth < 0) {
        return { year: prev.year - 1, month: 11 };
      } else if (newMonth > 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: newMonth };
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'prevu': return 'bg-blue-500 text-white';
      case 'present': return 'bg-green-500 text-white';
      case 'en_attente_validation': return 'bg-amber-500 text-white';
      case 'refus_validation': return 'bg-red-700 text-white';
      case 'repos': return 'bg-gray-400 text-white';
      case 'repos_force': return 'bg-fuchsia-700 text-white';
      case 'disponible': return 'bg-cyan-500 text-white';
      case 'repos_demande': return 'bg-amber-500 text-white';
      case 'absent': return 'bg-red-500 text-white';
      case 'formation_binome': return 'bg-orange-500 text-white';
      case 'formation_amzl': return 'bg-teal-500 text-white';
      case 'formation_safety': return 'bg-emerald-600 text-white';  // Formation Safety
      // Statuts abrégés
      case 'cp': return 'bg-indigo-500 text-white';      // Congés payés
      case 'css': return 'bg-violet-500 text-white';     // Congé sans solde
      case 'am': return 'bg-rose-500 text-white';        // Arrêt maladie
      case 'map': return 'bg-yellow-600 text-white';     // Mise à pied
      case 'fdc': return 'bg-slate-600 text-white';      // Fin de contrat
      case 'fpe': return 'bg-zinc-500 text-white';       // Fin de période d'essai
      case 'dem': return 'bg-stone-500 text-white';      // Démission
      case 'adp': return 'bg-red-700 text-white';        // Abandon de poste
      case 'licenciement': return 'bg-red-950 text-white';
      case 'rupture_conventionnelle': return 'bg-zinc-800 text-white';
      case 'en_cours_integration': return 'bg-slate-500 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  const getStatusLabel = (status) => {
    const pendingLabel = isMobileViewport ? 'À valider' : 'En attente de validation';
    const integrationLabel = isMobileViewport ? 'Intégration' : 'En cours d’intégration';
    const statusMap = {
      prevu: 'Prévu',
      present: 'Présent',
      en_attente_validation: pendingLabel,
      refus_validation: 'Refus de validation',
      repos: 'Repos',
      repos_force: 'Repos forcé',
      disponible: 'Disponible',
      repos_demande: 'Repos (demandé)',
      absent: 'Absent',
      formation_binome: 'Formation Binôme',
      formation_amzl: 'Formation AMZL',
      formation_safety: 'Formation Safety',
      en_cours_integration: integrationLabel,
      cp: 'CP',
      css: 'CSS',
      am: 'AM',
      map: 'MAP',
      fdc: 'FDC',
      fpe: 'FPE',
      dem: 'DEM',
      adp: 'ADP',
      licenciement: 'Licenciement',
      rupture_conventionnelle: 'Rupture conventionnelle'
    };
    return statusMap[status] || status;
  };

  const formatDayHeader = (dateStr) => {
    const date = parseLocalDate(dateStr);
    const todayStr = formatLocalDate(new Date());
    const isToday = dateStr === todayStr;
    const isPast = dateStr < todayStr;
    const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
    const dayName = dayNames[date.getDay()];
    return { day: dayName, date: date.getDate(), isToday, isPast };
  };

  const formatArchivePlanning = (planning = []) => {
    return planning.map((day) => {
      const dayLabel = parseLocalDate(day.date).toLocaleDateString('fr-FR', { weekday: 'short' });
      const timePart = day.loading_time ? ` ${day.loading_time}` : '';
      const rawStatus = day.status || 'repos';
      return `${dayLabel}: ${rawStatus === 'repos_force' ? 'Repos forcé' : rawStatus}${timePart}`;
    }).join(' | ');
  };

  const hasMissingPhotos = (userId) => missingPhotos.some(m => m.user.user_id === userId);

  // Handle quick deactivation of employee from planning
  const handleQuickDeactivate = async () => {
    if (!deactivateModal.employee) return;
    
    setDeactivating(true);
    try {
      await axios.put(`${API}/users/${deactivateModal.employee.user_id}`, {
        active: false
      }, { withCredentials: true });
      
      // Remove employee from local state
      setEmployees(prev => prev.filter(e => e.user_id !== deactivateModal.employee.user_id));
      notifyEmployeeActivityChange();
      setDeactivateModal({ open: false, employee: null });
      
    } catch (error) {
      console.error('Failed to deactivate employee:', error);
      alert('Erreur lors de la désactivation');
    } finally {
      setDeactivating(false);
    }
  };

  // Format employee name: "Nom Prénom" format, sorted by last name
  const formatEmployeeName = (emp) => {
    if (emp.last_name && emp.first_name) {
      return `${emp.last_name} ${emp.first_name}`;
    }
    return emp.name || 'Inconnu';
  };

  // Get initials for avatar
  const getInitials = (emp) => {
    if (emp.last_name && emp.first_name) {
      return `${emp.last_name.charAt(0)}${emp.first_name.charAt(0)}`.toUpperCase();
    }
    return emp.name?.charAt(0)?.toUpperCase() || '?';
  };

  // Sort employees alphabetically by last name, then first name
  const sortEmployeesAlphabetically = (employeesList) => {
    return [...employeesList].sort((a, b) => {
      const lastNameA = (a.last_name || a.name || '').toLowerCase();
      const lastNameB = (b.last_name || b.name || '').toLowerCase();
      if (lastNameA !== lastNameB) {
        return lastNameA.localeCompare(lastNameB);
      }
      const firstNameA = (a.first_name || '').toLowerCase();
      const firstNameB = (b.first_name || '').toLowerCase();
      return firstNameA.localeCompare(firstNameB);
    });
  };

  // Count planned missions per employee for the displayed period (week or month)
  // IMPORTANT: Excludes rejected missions
  const getPlannedCount = (userId) => {
    let count = 0;
    for (const date of displayDays) {
      const schedule = schedules[date]?.[userId];
      if (isCountableSchedule(schedule)) {
        count++;
      }
    }
    return count;
  };

  // Count missions per employee for the month
  // IMPORTANT: Only count missions that are NOT rejected
  const getMonthlyMissionsCount = (userId) => {
    const userMonthlySchedules = monthlySchedules[userId] || [];
    return userMonthlySchedules.filter(s => 
      s.status === 'present' && (s.validation_status || '').toLowerCase() === 'approved'
    ).length;
  };

  // Group employees by site and separate Colis Privé from Amazon
  const getGroupedEmployees = () => {
    const sitesMap = {};
    sites.forEach(site => {
      sitesMap[site.site_id] = site.name;
    });

    // Separate Colis Privé from others (Amazon)
    const colisPriveEmployees = employees.filter(emp => {
      const siteName = sitesMap[emp.site_id] || '';
      return siteName.toLowerCase().includes('colis') || siteName.toLowerCase().includes('privé') || siteName.toLowerCase().includes('prive');
    });

    const amazonEmployees = employees.filter(emp => {
      const siteName = sitesMap[emp.site_id] || '';
      const isColisPrivé = siteName.toLowerCase().includes('colis') || siteName.toLowerCase().includes('privé') || siteName.toLowerCase().includes('prive');
      return !isColisPrivé;
    });

    // Group Amazon employees by site and sort alphabetically
    const amazonBySite = {};
    amazonEmployees.forEach(emp => {
      const siteId = emp.site_id || 'non_assigne';
      const siteName = sitesMap[emp.site_id] || 'Non assigné';
      if (!amazonBySite[siteId]) {
        amazonBySite[siteId] = { name: siteName, employees: [] };
      }
      amazonBySite[siteId].employees.push(emp);
    });
    // Sort employees within each site
    Object.values(amazonBySite).forEach(site => {
      site.employees = sortEmployeesAlphabetically(site.employees);
    });

    // Group Colis Privé employees by site and sort alphabetically
    const colisPriveBySite = {};
    colisPriveEmployees.forEach(emp => {
      const siteId = emp.site_id || 'non_assigne';
      const siteName = sitesMap[emp.site_id] || 'Non assigné';
      if (!colisPriveBySite[siteId]) {
        colisPriveBySite[siteId] = { name: siteName, employees: [] };
      }
      colisPriveBySite[siteId].employees.push(emp);
    });
    // Sort employees within each site
    Object.values(colisPriveBySite).forEach(site => {
      site.employees = sortEmployeesAlphabetically(site.employees);
    });

    return { amazonBySite, colisPriveBySite, sitesMap };
  };

  // Memoize grouped employees to prevent recalculation on every render
  const { amazonBySite, colisPriveBySite, sitesMap } = useMemo(() => getGroupedEmployees(), [employees, sites]);

  // Count scheduled employees per site for a specific date
  // Excludes rejected missions
  const getSiteCountForDate = useCallback((siteEmployees, date) => {
    return siteEmployees.filter(emp => {
      const schedule = schedules[date]?.[emp.user_id];
      return isCountableSchedule(schedule);
    }).length;
  }, [schedules]);

  // Count total scheduled employees per site for the displayed period
  const getSiteWeekTotal = useCallback((siteEmployees) => {
    let total = 0;
    displayDays.forEach(date => {
      total += getSiteCountForDate(siteEmployees, date);
    });
    return total;
  }, [displayDays, getSiteCountForDate]);

  // Render inline day headers (to repeat before each section)
  const renderInlineDayHeaders = (bgColor = 'bg-gray-100') => (
    <tr className={`${bgColor} border-b-2`}>
      <td className="p-2 font-semibold text-gray-600 text-xs sticky left-0 z-10" style={{ backgroundColor: bgColor === 'bg-blue-100' ? '#dbeafe' : bgColor === 'bg-purple-100' ? '#f3e8ff' : '#f3f4f6' }}>
        📅 JOURS
      </td>
      <td className="p-2 text-xs text-gray-500" style={{ backgroundColor: bgColor === 'bg-blue-100' ? '#dbeafe' : bgColor === 'bg-purple-100' ? '#f3e8ff' : '#f3f4f6' }}>
        Site
      </td>
      {displayDays.map(date => {
        const { day, date: dayNum, isToday, isPast } = formatDayHeader(date);
        return (
          <td key={`inline-${date}`} className={`p-1 text-center ${isToday ? 'bg-[#0F172A] text-white' : isPast ? 'bg-gray-300' : ''}`}>
            <div className="text-xs font-bold uppercase">{day}</div>
            <div className={`font-bold ${viewMode === 'month' ? 'text-xs' : 'text-sm'}`}>{dayNum}</div>
          </td>
        );
      })}
      <td className="p-1 text-center text-xs bg-blue-100 font-semibold">Sem.</td>
      <td className="p-1 text-center text-xs bg-green-100 font-semibold">Mois</td>
    </tr>
  );

  // Render employee row
  const renderEmployeeRow = (emp) => {
    const hasNoContract = !emp.contract_hours;
    const rowBgClass = hasNoContract ? 'bg-red-50' : '';
    const cellBgClass = hasNoContract ? 'bg-red-50' : 'bg-white';
    
    return (
    <tr key={emp.user_id} className={`border-b hover:bg-gray-50 ${rowBgClass}`} title={hasNoContract ? 'Contrat non signé - Type de contrat non renseigné' : ''}>
      <td className={`p-2 sticky left-0 ${cellBgClass} z-10 ${hasNoContract ? 'border-l-4 border-l-red-500' : ''}`}>
        <div className="flex items-center gap-2">
          {emp.picture ? (
            <img src={emp.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className={`w-8 h-8 ${hasNoContract ? 'bg-red-500' : 'bg-[#0F172A]'} rounded-full flex items-center justify-center`}>
              <span className="text-white text-xs font-medium">{getInitials(emp)}</span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className={`font-medium text-sm truncate ${hasNoContract ? 'text-red-700' : 'text-[#0F172A]'}`}>{formatEmployeeName(emp)}</p>
            {emp.contract_hours ? (
              <p className="text-xs text-blue-600">{emp.contract_hours}h/sem</p>
            ) : (
              <p className="text-xs text-red-500 font-medium">⚠ Contrat non signé</p>
            )}
          </div>
          {/* Quick deactivate button */}
          <button
            onClick={() => setDeactivateModal({ open: true, employee: emp })}
            className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
            title="Désactiver ce salarié"
            data-testid={`deactivate-btn-${emp.user_id}`}
          >
            <UserX className="w-4 h-4" />
          </button>
        </div>
      </td>
      <td className="p-2">
        <select
          value={emp.site_id || ''}
          onChange={(e) => handleAssignSite(emp.user_id, e.target.value || null)}
          data-testid={`planning-site-select-${emp.user_id}`}
          className="text-xs px-2 py-1 border rounded w-full"
        >
          <option value="">Non assigné</option>
          {sites.map(site => (
            <option key={site.site_id} value={site.site_id}>
              {site.name}
            </option>
          ))}
        </select>
      </td>
      {displayDays.map(date => {
        const schedule = schedules[date]?.[emp.user_id] || { status: 'repos' };  // Default: repos
        const { isPast } = formatDayHeader(date);
        const hasAdminComment = !!schedule.admin_comment;
        const currentStatus = schedule.status || 'repos';
        const displayStatusFromValidation = getValidationAwareStatus(schedule, currentStatus);
        const siteName = sites.find(s => s.site_id === emp.site_id)?.name || '';
        const isAmazonEmployee = !!emp.site_id && isAmazonSiteName(siteName);
        const hasNextFallback = isAmazonEmployee && hasNextFallbackSite(siteName);
        const dayOfWeek = parseLocalDate(date).getDay();
        const isWeekendOnlyEmployee = emp.work_pattern === 'weekend_only';
        const isLockedWeekdayForWeekendOnly = isWeekendOnlyEmployee && isAmazonEmployee && dayOfWeek >= 1 && dayOfWeek <= 4;
        const effectiveStatus = isLockedWeekdayForWeekendOnly ? 'repos_demande' : displayStatusFromValidation;
        const statusForWorkLogic = isLockedWeekdayForWeekendOnly ? 'repos_demande' : currentStatus;
        const displayedTourType = (schedule.tour_type && schedule.tour_type.trim() !== '')
          ? schedule.tour_type
          : (hasNextFallback && statusForWorkLogic === 'prevu' ? 'NEXT' : '');
        const autoTimesConfig = getAmazonAutoTimes(displayedTourType);
        const fallbackNextTime = hasNextFallback ? (getAutoLoadingTime(siteName, date) || '') : '';
        const displayedStartTime = (displayedTourType === 'NEXT')
          ? (schedule.start_time || fallbackNextTime)
          : (schedule.tour_type ? (schedule.start_time || '') : '');
        const displayedSecondStartTime = schedule.tour_type
          ? (schedule.second_start_time || autoTimesConfig?.starts?.[1]?.time || '')
          : '';
        const isIntegrationPlaceholder = (statusForWorkLogic || '').toLowerCase() === 'en_cours_integration' || (displayedTourType || '').toLowerCase().includes('en cours d’intégration') || (displayedTourType || '').toLowerCase().includes("en cours d'integration");
        const hasAmazonPrestation = isAmazonEmployee && !!displayedTourType && !isIntegrationPlaceholder;
        const hasMultipleStarts = (autoTimesConfig?.starts?.length || 0) > 1 || !!schedule.second_start_time;
        const isCurrentStatusAllowedForLimited = limitedPlanningAllowedStatuses.includes((effectiveStatus || '').toLowerCase());
        
        // Statuts où le salarié ne travaille pas (ne pas afficher "Rdv Next")
        const isNotWorkingStatus = ['repos', 'repos_force', 'disponible', 'repos_demande', 'absent', 'cp', 'css', 'am', 'fdc', 'fpe', 'dem', 'adp', 'map', 'licenciement', 'rupture_conventionnelle', 'en_cours_integration'].includes(statusForWorkLogic);
        
        // Only show comment if it's "Rdv Next" type (not prestation data) AND employee is working
        const isRdvComment = schedule.comment && 
          (schedule.comment.includes('Rdv') || schedule.comment === 'Rdv Next') &&
          !schedule.comment.includes('Tournée:') && 
          !schedule.comment.includes('Livrés:') &&
          !schedule.comment.includes('Retours:') &&
          !schedule.comment.includes('Collectés:');
        const hasRdvComment = isRdvComment && !isNotWorkingStatus && !(isAmazonEmployee && statusForWorkLogic === 'prevu');
        return (
          <td key={`${emp.user_id}-${date}`} data-cell-state={schedule._conflict ? 'conflict' : schedule._touched ? 'unsaved' : 'saved'} className={`p-1 text-center ${isPast ? 'bg-gray-50' : ''} ${schedule._conflict ? 'ring-2 ring-inset ring-red-500' : schedule._touched ? 'ring-2 ring-inset ring-amber-400' : ''}`}>
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <select
                  key={`status-${emp.user_id}-${date}`}
                  value={effectiveStatus}
                  onChange={(e) => updateSchedule(date, emp.user_id, 'status', e.target.value)}
                  className={`flex-1 text-[10px] sm:text-xs p-1 rounded border-0 cursor-pointer leading-tight whitespace-normal break-words max-w-[120px] sm:max-w-none ${getStatusColor(effectiveStatus)}`}
                  disabled={saving || isLockedWeekdayForWeekendOnly}
                  data-testid={`planning-status-${emp.user_id}-${date}`}
                >
                  {isLimitedPlanningAdmin ? (
                    <>
                      {!isCurrentStatusAllowedForLimited && effectiveStatus && (
                        <option value={effectiveStatus} disabled>
                          {getStatusLabel(effectiveStatus)} (lecture seule)
                        </option>
                      )}
                      <option value="en_attente_validation" disabled>{isMobileViewport ? 'À valider (lecture seule)' : 'En attente de validation (lecture seule)'}</option>
                      <option value="refus_validation" disabled>Refus de validation (lecture seule)</option>
                      <option value="prevu">Prévu</option>
                      <option value="absent">Absent</option>
                      <option value="repos">Repos</option>
                      <option value="repos_force">Repos forcé</option>
                    </>
                  ) : (
                    <>
                      <option value="en_attente_validation" disabled>{isMobileViewport ? 'À valider' : 'En attente de validation'}</option>
                      <option value="refus_validation" disabled>Refus de validation</option>
                      <option value="prevu">Prévu</option>
                      <option value="present">Présent</option>
                      <option value="repos">Repos</option>
                      <option value="repos_force">Repos forcé</option>
                      <option value="disponible">Disponible</option>
                      <option value="repos_demande">Repos (demandé)</option>
                      <option value="absent">Absent</option>
                      <option value="formation_binome">Formation Binôme</option>
                      <option value="formation_amzl">Formation AMZL</option>
                      <option value="formation_safety">Formation Safety</option>
                      <option value="en_cours_integration">{isMobileViewport ? 'Intégration' : 'En cours d’intégration'}</option>
                      <option value="cp">CP</option>
                      <option value="css">CSS</option>
                      <option value="am">AM</option>
                      <option value="map">MAP</option>
                      <option value="fdc">FDC</option>
                      <option value="fpe">FPE</option>
                      <option value="dem">DEM</option>
                      <option value="adp">ADP</option>
                      <option value="licenciement">Licenciement</option>
                      <option value="rupture_conventionnelle">Rupture conventionnelle</option>
                    </>
                  )}
                </select>
                {viewMode === 'week' && !(isAmazonEmployee && statusForWorkLogic === 'prevu') && (
                  <button
                    onClick={() => openCommentModal(emp.user_id, date)}
                    className={`p-1 rounded transition-colors ${hasAdminComment ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'}`}
                    title={hasAdminComment ? 'Modifier le commentaire' : 'Ajouter un commentaire'}
                    disabled={isLockedWeekdayForWeekendOnly}
                    data-testid={`planning-comment-button-${emp.user_id}-${date}`}
                  >
                    <MessageSquare className="w-3 h-3" />
                  </button>
                )}
              </div>
              {viewMode === 'week' && !isAmazonEmployee && (statusForWorkLogic === 'prevu' || statusForWorkLogic === 'present') && (
                <input
                  key={`time-${emp.user_id}-${date}`}
                  type="time"
                  value={schedule.loading_time || ''}
                  onChange={(e) => updateSchedule(date, emp.user_id, 'loading_time', e.target.value)}
                  className="w-full text-xs p-1 border rounded"
                  placeholder="Heure"
                  disabled={saving || isLockedWeekdayForWeekendOnly}
                />
              )}
              {viewMode === 'week' && isAmazonEmployee && statusForWorkLogic === 'prevu' && (
                <div className="space-y-1">
                  <select
                    value={displayedTourType}
                    onChange={(e) => updateSchedule(date, emp.user_id, 'tour_type', e.target.value)}
                    className="w-full text-xs p-1 border rounded"
                    data-testid={`planning-prestation-type-${emp.user_id}-${date}`}
                  >
                    <option value="">{hasNextFallback ? 'Auto (NEXT)' : 'Prestation Amazon...'}</option>
                    <option value="NEXT">NEXT</option>
                    <option value="SDA">SDA</option>
                    <option value="SDB">SDB</option>
                    <option value="SDC">SDC</option>
                    <option value="SDA+C">SDA+C</option>
                    <option value="SDB+C">SDB+C</option>
                    <option value="SDA+B">SDA+B</option>
                    <option value="Assistance chef d'équipe">Assistance chef d&apos;équipe</option>
                    <option value="Backup">Backup</option>
                  </select>
                  {displayedTourType && getAmazonAutoTimes(displayedTourType).display && (
                    <div
                      className="text-[11px] px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200"
                      data-testid={`planning-prestation-auto-time-${emp.user_id}-${date}`}
                    >
                      Prises de service auto: {getAmazonAutoTimes(displayedTourType).display}
                    </div>
                  )}
                  <div className="text-[10px] text-gray-500">Prise de service 1{autoTimesConfig?.starts?.[0]?.label ? ` (${autoTimesConfig.starts[0].label})` : ''}</div>
                  <input
                    type="time"
                    value={displayedStartTime}
                    onChange={(e) => updateSchedule(date, emp.user_id, 'start_time', e.target.value)}
                    className="w-full text-xs p-1 border rounded"
                    placeholder="Prise de service"
                    data-testid={`planning-prestation-time-${emp.user_id}-${date}`}
                  />
                  {hasMultipleStarts && (
                    <>
                      <div className="text-[10px] text-gray-500">Prise de service 2{autoTimesConfig?.starts?.[1]?.label ? ` (${autoTimesConfig.starts[1].label})` : ''}</div>
                      <input
                        type="time"
                        value={displayedSecondStartTime}
                        onChange={(e) => updateSchedule(date, emp.user_id, 'second_start_time', e.target.value)}
                        className="w-full text-xs p-1 border rounded"
                        placeholder="Prise de service 2"
                        data-testid={`planning-prestation-time-2-${emp.user_id}-${date}`}
                      />
                    </>
                  )}
                </div>
              )}
              {/* Show "Rdv Next" comment only if employee is working (prevu, present, formation) */}
              {viewMode === 'week' && hasRdvComment && (
                <div className="text-xs text-blue-600 bg-blue-50 px-1 py-0.5 rounded truncate font-medium" title={schedule.comment}>
                  📍 {schedule.comment}
                </div>
              )}
              {/* Show admin comment if present */}
              {viewMode === 'week' && hasAdminComment && (
                <div className="text-xs text-orange-600 bg-orange-50 px-1 py-0.5 rounded truncate" title={schedule.admin_comment}>
                  💬 {schedule.admin_comment.substring(0, 15)}{schedule.admin_comment.length > 15 ? '...' : ''}
                </div>
              )}
            </div>
          </td>
        );
      })}
      <td className="p-2 text-center bg-blue-50">
        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
          {getPlannedCount(emp.user_id)}
        </span>
      </td>
      <td className="p-2 text-center bg-green-50">
        <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full font-bold text-sm">
          {getMonthlyMissionsCount(emp.user_id)}
        </span>
      </td>
    </tr>
  );
  };

  // Render site totals row
  const renderSiteTotalsRow = (siteEmployees, siteName, bgColor) => (
    <tr className={`${bgColor} font-semibold border-b-2`}>
      <td colSpan={2} className="p-2 text-right text-sm sticky left-0 z-10" style={{ backgroundColor: 'inherit' }}>
        Total {siteName}:
      </td>
      {displayDays.map(date => {
        const count = getSiteCountForDate(siteEmployees, date);
        return (
          <td key={`total-${siteName}-${date}`} className="p-2 text-center">
            <span className={`inline-flex items-center justify-center ${viewMode === 'month' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} bg-white rounded-full font-bold shadow`}>
              {count}
            </span>
          </td>
        );
      })}
      <td className="p-2 text-center">
        <span className="inline-flex items-center justify-center px-3 py-1 bg-white rounded-full font-bold text-sm shadow">
          {getSiteWeekTotal(siteEmployees)}
        </span>
      </td>
      <td className="p-2 text-center">-</td>
    </tr>
  );

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-4 fade-in pb-36">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Planning de l&apos;équipe</h1>
            <p className="text-gray-500">
              Prévu → Présent = 1 mission | 
              {viewMode === 'month' 
                ? new Date(currentMonth.year, currentMonth.month).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                : `Mois: ${parseLocalDate(weekStart).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'week' && isSuperAdmin && (
              <>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${weekPublication.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                  data-testid="admin-planning-week-status-badge"
                >
                  {weekPublication.status === 'published' ? 'Publié' : 'Brouillon'}
                </span>
                <Button
                  onClick={publishCurrentWeek}
                  disabled={publishingWeek}
                  variant="outline"
                  className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                  data-testid="publish-planning-week-button"
                >
                  {publishingWeek ? 'Publication...' : 'Publier le planning'}
                </Button>
              </>
            )}
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border overflow-hidden">
              <Button 
                variant={viewMode === 'week' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('week')}
                data-testid="planning-view-week"
                className={viewMode === 'week' ? 'rounded-none' : 'rounded-none'}
              >
                Semaine
              </Button>
              <Button 
                variant={viewMode === 'month' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('month')}
                data-testid="planning-view-month"
                className={viewMode === 'month' ? 'rounded-none' : 'rounded-none'}
              >
                Mois
              </Button>
            </div>
            
            {/* Send Planning Notifications Button */}
            {isSuperAdmin && (
              <Button 
                onClick={() => setEmailModal({ open: true })} 
                variant="outline"
                className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="send-planning-notifications-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer les notifications
              </Button>
            )}

            <Button
              onClick={openArchiveModal}
              variant="outline"
              className="bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="open-published-planning-archive-button"
            >
              Planning publié / Archive
            </Button>

            {isSuperAdmin && (
              <a href="/admin/planning/integrity" className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="planning-integrity-link">
                Doublons et récupération
                {duplicateCount > 0 && <span className="ml-2 rounded-full bg-red-600 px-2 text-xs font-semibold text-white" data-testid="planning-duplicate-count">{duplicateCount}</span>}
              </a>
            )}
          </div>
        </div>

        <PlanningSaveStatus state={saveState} pending={pendingCells.length} conflicts={conflictCells.length}
          refreshing={refreshing || periodLoading} lastSync={lastSync} onSave={saveAllSchedules} onDiscard={discardChanges} />
        {sessionLost && (
          <SessionLostBanner pending={pendingCells.length} schedules={schedules} onDismiss={() => setSessionLost(false)}
            onRetry={() => { setSessionLost(false); saveAllSchedules(); }} />
        )}
        {draftRestored && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="planning-draft-restored">
            <span>{draftRestored.count} saisie(s) non enregistrée(s) de votre session précédente ({new Date(draftRestored.saved_at).toLocaleString('fr-FR')}) ont été récupérées. Vérifiez-les puis cliquez sur « Enregistrer ».</span>
            <button className="ml-auto underline" onClick={() => setDraftRestored(null)} data-testid="planning-draft-restored-dismiss">Compris</button>
          </div>
        )}
        <PlanningConflictsPanel conflicts={conflictCells} employees={employees}
          onKeepMine={(item) => resolveConflict(item, true)} onTakeServer={(item) => resolveConflict(item, false)} />

        {/* Lien vers la gestion des sites */}
        <Card className="mb-4 bg-blue-50 border-2 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Gérer les sites/sociétés</h3>
                <p className="text-sm text-gray-600">Créer et supprimer des sites ({sites.length} sites)</p>
              </div>
              <a
                href="/admin/sites"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700"
              >
                GÉRER →
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Week/Month Navigation */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              {viewMode === 'week' ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => changeWeek(-1)} data-testid="planning-prev-week">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Sem. préc.
                  </Button>
                  <div className="text-center">
                    <p className="font-semibold text-[#0F172A]">
                      Semaine du {parseLocalDate(weekStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                    </p>
                    {weekPublication.status === 'published' && weekPublication.published_at && (
                      <p className="text-xs text-green-600" data-testid="admin-planning-week-published-date">
                        Publié le {new Date(weekPublication.published_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => changeWeek(1)} data-testid="planning-next-week">
                    Sem. suiv. <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} data-testid="planning-prev-month">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Mois préc.
                  </Button>
                  <p className="font-semibold text-[#0F172A]">
                    {new Date(currentMonth.year, currentMonth.month).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => changeMonth(1)} data-testid="planning-next-month">
                    Mois suiv. <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Planning Grid */}
        {loading && !hasLoadedOnce.current ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : loadError ? (
          <Card data-testid="planning-load-error">
            <CardContent className="p-8 text-center">
              <p className="mb-3 font-medium text-red-700">Le planning n'a pas pu être chargé (connexion ou session). Aucune donnée n'a été modifiée.</p>
              <Button onClick={() => fetchData()} data-testid="planning-load-retry">Réessayer</Button>
            </CardContent>
          </Card>
        ) : employees.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Aucun employé approuvé</p>
            </CardContent>
          </Card>
        ) : (
          <Card className={`overflow-hidden transition-opacity ${periodLoading ? 'pointer-events-none opacity-50' : ''}`} aria-busy={periodLoading} data-testid="admin-planning-grid">
            <div className="overflow-x-auto">
              <table className={`w-full ${viewMode === 'month' ? 'min-w-[2000px]' : 'min-w-[1000px]'}`}>
                <thead className="sticky top-0 z-20 bg-white">
                  <tr className="border-b bg-gray-50 shadow-md">
                    <th className="text-left p-3 font-medium text-gray-600 w-48 sticky left-0 bg-gray-50 z-30 border-r">Salarié</th>
                    <th className="text-left p-2 font-medium text-gray-600 w-40 bg-gray-50 border-r">Site/Société</th>
                    {displayDays.map(date => {
                      const { day, date: dayNum, isToday, isPast } = formatDayHeader(date);
                      return (
                        <th key={date} className={`p-2 text-center font-medium ${viewMode === 'month' ? 'w-12' : 'w-28'} border-r ${isToday ? 'bg-[#0F172A] text-white' : isPast ? 'bg-gray-200' : 'bg-gray-50 text-gray-600'}`}>
                          <div className="text-xs uppercase font-bold">{day}</div>
                          <div className={`font-bold ${viewMode === 'month' ? 'text-sm' : 'text-lg'}`}>{dayNum}</div>
                        </th>
                      );
                    })}
                    <th className="text-center p-2 font-medium text-gray-600 w-20 bg-blue-100 border-r">
                      <div className="text-xs font-bold">Prévisions</div>
                      <div className="text-xs">{viewMode === 'month' ? 'Période' : 'Semaine'}</div>
                    </th>
                    <th className="text-center p-2 font-medium text-gray-600 w-20 bg-green-100">
                      <div className="text-xs font-bold">Missions</div>
                      <div className="text-xs">Mois</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* ==================== SECTION AMAZON ==================== */}
                  {Object.keys(amazonBySite).length > 0 && (
                    <>
                      <tr className="bg-blue-600 text-white">
                        <td colSpan={2 + displayDays.length + 2} className="p-3 font-bold text-lg sticky left-0" style={{ backgroundColor: '#2563eb' }}>
                          🚚 AMAZON ({Object.values(amazonBySite).reduce((acc, site) => acc + site.employees.length, 0)} chauffeurs)
                        </td>
                      </tr>
                      {Object.entries(amazonBySite).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([siteId, siteData]) => (
                        <React.Fragment key={`amazon-${siteId}`}>
                          {/* Site header */}
                          <tr className="bg-blue-50 border-t-2 border-blue-300">
                            <td colSpan={2 + displayDays.length + 2} className="p-2 font-semibold text-blue-800 sticky left-0" style={{ backgroundColor: '#eff6ff' }}>
                              📍 {siteData.name} ({siteData.employees.length} chauffeurs)
                            </td>
                          </tr>
                          {/* Day headers for this site */}
                          {renderInlineDayHeaders('bg-blue-100')}
                          {/* Employees */}
                          {siteData.employees.map(emp => renderEmployeeRow(emp))}
                          {/* Site totals */}
                          {renderSiteTotalsRow(siteData.employees, siteData.name, 'bg-blue-50')}
                        </React.Fragment>
                      ))}
                      {/* TOTAL AMAZON */}
                      <tr className="bg-blue-700 text-white font-bold border-t-2 border-blue-800">
                        <td colSpan={2} className="p-2 text-right sticky left-0" style={{ backgroundColor: '#1d4ed8' }}>
                          TOTAL AMAZON:
                        </td>
                        {displayDays.map(date => {
                          const allAmazonEmployees = Object.values(amazonBySite).flatMap(site => site.employees);
                          const count = allAmazonEmployees.filter(emp => isCountableSchedule(schedules[date]?.[emp.user_id])).length;
                          return (
                            <td key={`amazon-total-${date}`} className="p-2 text-center">
                              <span className={`inline-flex items-center justify-center ${viewMode === 'month' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} bg-white text-blue-700 rounded-full font-bold shadow`}>
                                {count}
                              </span>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 bg-white text-blue-700 rounded-full font-bold text-sm shadow">
                            {displayDays.reduce((acc, date) => {
                              const allAmazonEmployees = Object.values(amazonBySite).flatMap(site => site.employees);
                              return acc + allAmazonEmployees.filter(emp => isCountableSchedule(schedules[date]?.[emp.user_id])).length;
                            }, 0)}
                          </span>
                        </td>
                        <td className="p-2 text-center">-</td>
                      </tr>
                    </>
                  )}

                  {/* ==================== SECTION COLIS PRIVÉ ==================== */}
                  {Object.keys(colisPriveBySite).length > 0 && (
                    <>
                      <tr className="bg-purple-600 text-white">
                        <td colSpan={2 + displayDays.length + 2} className="p-3 font-bold text-lg sticky left-0" style={{ backgroundColor: '#9333ea' }}>
                          📦 COLIS PRIVE ({Object.values(colisPriveBySite).reduce((acc, site) => acc + site.employees.length, 0)} chauffeurs)
                        </td>
                      </tr>
                      {Object.entries(colisPriveBySite).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([siteId, siteData]) => (
                        <React.Fragment key={`colisprive-${siteId}`}>
                          {/* Site header */}
                          <tr className="bg-purple-50 border-t-2 border-purple-300">
                            <td colSpan={2 + displayDays.length + 2} className="p-2 font-semibold text-purple-800 sticky left-0" style={{ backgroundColor: '#faf5ff' }}>
                              📍 {siteData.name} ({siteData.employees.length} chauffeurs)
                            </td>
                          </tr>
                          {/* Day headers for this site */}
                          {renderInlineDayHeaders('bg-purple-100')}
                          {/* Employees */}
                          {siteData.employees.map(emp => renderEmployeeRow(emp))}
                          {/* Site totals */}
                          {renderSiteTotalsRow(siteData.employees, siteData.name, 'bg-purple-50')}
                        </React.Fragment>
                      ))}
                      {/* TOTAL COLIS PRIVE */}
                      <tr className="bg-purple-700 text-white font-bold border-t-2 border-purple-800">
                        <td colSpan={2} className="p-2 text-right sticky left-0" style={{ backgroundColor: '#7e22ce' }}>
                          TOTAL COLIS PRIVE:
                        </td>
                        {displayDays.map(date => {
                          const allColisEmployees = Object.values(colisPriveBySite).flatMap(site => site.employees);
                          const count = allColisEmployees.filter(emp => isCountableSchedule(schedules[date]?.[emp.user_id])).length;
                          return (
                            <td key={`colis-total-${date}`} className="p-2 text-center">
                              <span className={`inline-flex items-center justify-center ${viewMode === 'month' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} bg-white text-purple-700 rounded-full font-bold shadow`}>
                                {count}
                              </span>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 bg-white text-purple-700 rounded-full font-bold text-sm shadow">
                            {displayDays.reduce((acc, date) => {
                              const allColisEmployees = Object.values(colisPriveBySite).flatMap(site => site.employees);
                              return acc + allColisEmployees.filter(emp => isCountableSchedule(schedules[date]?.[emp.user_id])).length;
                            }, 0)}
                          </span>
                        </td>
                        <td className="p-2 text-center">-</td>
                      </tr>
                    </>
                  )}

                  {/* ==================== GRAND TOTAL ==================== */}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td colSpan={2} className="p-3 text-right sticky left-0" style={{ backgroundColor: '#1f2937' }}>
                      TOTAL GENERAL ({employees.length} chauffeurs):
                    </td>
                    {displayDays.map(date => {
                      const totalCount = employees.filter(emp => isCountableSchedule(schedules[date]?.[emp.user_id])).length;
                      return (
                        <td key={`grand-total-${date}`} className="p-2 text-center">
                          <span className={`inline-flex items-center justify-center ${viewMode === 'month' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-lg'} bg-white text-gray-800 rounded-full font-bold shadow-lg`}>
                            {totalCount}
                          </span>
                        </td>
                      );
                    })}
                    <td className="p-2 text-center">
                      <span className="inline-flex items-center justify-center px-4 py-2 bg-white text-gray-800 rounded-full font-bold text-lg shadow-lg">
                        {displayDays.reduce((acc, date) => {
                          return acc + employees.filter(emp => isCountableSchedule(schedules[date]?.[emp.user_id])).length;
                        }, 0)}
                      </span>
                    </td>
                    <td className="p-2 text-center">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Legend */}
        {canManageInternal(user) && <InternalPlanningSection />}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Prévu</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Présent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-400 rounded"></div>
                <span>Repos</span>
              </div>
              <div className="flex items-center gap-2" data-testid="planning-legend-repos-force">
                <div className="w-4 h-4 bg-fuchsia-700 rounded"></div>
                <span>Repos forcé</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded"></div>
                <span>Repos demandé</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>Absent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>Form. Binôme</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-500 rounded"></div>
                <span>Form. AMZL</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-600 rounded"></div>
                <span>Form. Safety</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-indigo-500 rounded"></div>
                <span>CP</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-violet-500 rounded"></div>
                <span>CSS</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-rose-500 rounded"></div>
                <span>AM</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                <span>MAP</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-slate-600 rounded"></div>
                <span>FDC</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-500 rounded"></div>
                <span>FPE</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-stone-500 rounded"></div>
                <span>DEM</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-700 rounded"></div>
                <span>ADP</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comment Modal */}
      {commentModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#0F172A]">
                  Commentaire pour le {new Date(commentModal.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>
                <button
                  onClick={closeCommentModal}
                  data-testid="comment-modal-close"
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Ce commentaire sera visible par le salarié dans son planning
              </p>
            </div>
            <div className="p-4">
              <textarea
                value={commentModal.comment}
                data-testid="comment-modal-textarea"
                onChange={(e) => setCommentModal(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Ex: RDV médical à 14h, Livraison urgente secteur 5, Formation obligatoire..."
                className="w-full h-32 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-lg">
              <Button
                variant="outline"
                onClick={closeCommentModal}
                disabled={savingComment}
                data-testid="comment-modal-cancel"
              >
                Annuler
              </Button>
              <Button
                onClick={saveAdminComment}
                disabled={savingComment}
                data-testid="comment-modal-save"
                className="bg-[#0F172A] hover:bg-[#1e293b]"
              >
                {savingComment ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Notification Modal */}
      {emailModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Envoyer les notifications de planning
              </h3>
              <p className="text-blue-100 text-sm mt-1">
                Informer les livreurs que leur planning est disponible
              </p>
            </div>
            
            <div className="p-6">
              {!emailResult ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-blue-800 text-sm">
                      <strong>📧 Cette action va envoyer un email</strong> à tous les livreurs actifs, 
                      qu&apos;ils aient un planning prévu ou non.
                    </p>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-amber-800 text-sm">
                      <strong>⚠️ Important :</strong> Assurez-vous d&apos;avoir enregistré toutes vos 
                      modifications avant d&apos;envoyer les notifications.
                    </p>
                  </div>
                </>
              ) : emailResult.success ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      Notifications traitées !
                    </p>
                  </div>
                  
                  {/* Clickable stats */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div 
                      className={`bg-green-100 rounded-lg p-3 cursor-pointer hover:bg-green-200 transition-colors ${expandedList === 'sent' ? 'ring-2 ring-green-500' : ''}`}
                      onClick={() => setExpandedList(expandedList === 'sent' ? null : 'sent')}
                    >
                      <p className="text-2xl font-bold text-green-700">{emailResult.stats?.emails_sent || 0}</p>
                      <p className="text-xs text-green-600">Envoyés ▼</p>
                    </div>
                    <div 
                      className={`bg-gray-100 rounded-lg p-3 cursor-pointer hover:bg-gray-200 transition-colors ${expandedList === 'no_schedule' ? 'ring-2 ring-gray-500' : ''}`}
                      onClick={() => setExpandedList(expandedList === 'no_schedule' ? null : 'no_schedule')}
                    >
                      <p className="text-2xl font-bold text-gray-700">{emailResult.stats?.no_schedule_count || 0}</p>
                      <p className="text-xs text-gray-600">Sans planning ▼</p>
                    </div>
                    <div 
                      className={`bg-red-100 rounded-lg p-3 cursor-pointer hover:bg-red-200 transition-colors ${expandedList === 'failed' ? 'ring-2 ring-red-500' : ''}`}
                      onClick={() => setExpandedList(expandedList === 'failed' ? null : 'failed')}
                    >
                      <p className="text-2xl font-bold text-red-700">{emailResult.stats?.emails_failed || 0}</p>
                      <p className="text-xs text-red-600">Échoués ▼</p>
                    </div>
                  </div>
                  
                  {/* Expanded list */}
                  {expandedList && (
                    <div className="border rounded-lg max-h-60 overflow-y-auto">
                      <div className={`p-2 font-medium text-sm sticky top-0 ${
                        expandedList === 'sent' ? 'bg-green-100 text-green-800' :
                        expandedList === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {expandedList === 'sent' && `✅ Emails envoyés (${emailResult.details?.sent?.length || 0})`}
                        {expandedList === 'failed' && `❌ Emails échoués (${emailResult.details?.failed?.length || 0})`}
                        {expandedList === 'no_schedule' && `📭 Sans planning (${emailResult.details?.no_schedule?.length || 0})`}
                      </div>
                      <div className="divide-y">
                        {expandedList === 'sent' && emailResult.details?.sent?.map((emp, idx) => (
                          <div key={idx} className="p-2 text-sm flex justify-between items-center hover:bg-gray-50">
                            <span className="font-medium">{emp.name}</span>
                            <span className="text-gray-500 text-xs">{emp.email}</span>
                          </div>
                        ))}
                        {expandedList === 'failed' && emailResult.details?.failed?.map((emp, idx) => (
                          <div key={idx} className="p-2 text-sm hover:bg-gray-50">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{emp.name}</span>
                              <span className="text-gray-500 text-xs">{emp.email}</span>
                            </div>
                            {emp.error && (
                              <p className="text-red-600 text-xs mt-1">Erreur: {emp.error}</p>
                            )}
                          </div>
                        ))}
                        {expandedList === 'no_schedule' && emailResult.details?.no_schedule?.map((emp, idx) => (
                          <div key={idx} className="p-2 text-sm flex justify-between items-center hover:bg-gray-50">
                            <span className="font-medium">{emp.name}</span>
                            <span className="text-gray-500 text-xs">{emp.email}</span>
                          </div>
                        ))}
                        {((expandedList === 'sent' && !emailResult.details?.sent?.length) ||
                          (expandedList === 'failed' && !emailResult.details?.failed?.length) ||
                          (expandedList === 'no_schedule' && !emailResult.details?.no_schedule?.length)) && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Aucun livreur dans cette catégorie
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {emailResult.week && (
                    <p className="text-sm text-gray-600 text-center">
                      Semaine du {new Date(emailResult.week.start).toLocaleDateString('fr-FR')} au {new Date(emailResult.week.end).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 font-medium flex items-center gap-2">
                    <X className="w-5 h-5" />
                    Erreur lors de l&apos;envoi
                  </p>
                  <p className="text-red-600 text-sm mt-2">{emailResult.error}</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-lg">
              <Button
                variant="outline"
                onClick={() => { setEmailModal({ open: false }); setEmailResult(null); setExpandedList(null); }}
                disabled={sendingEmails}
              >
                {emailResult ? 'Fermer' : 'Annuler'}
              </Button>
              {!emailResult && (
                <Button
                  onClick={sendPlanningNotifications}
                  disabled={sendingEmails}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sendingEmails ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer maintenant
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Published Planning Archive Modal (read-only) */}
      {archiveModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[85vh] overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A]" data-testid="published-archive-modal-title">
                  Planning publié / Archive
                </h3>
                <p className="text-sm text-gray-600">
                  Semaine du {parseLocalDate(weekStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setArchiveModal({ open: false, loading: false, records: [] })}
                data-testid="close-published-archive-modal-button"
              >
                Fermer
              </Button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {archiveModal.loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
                </div>
              ) : archiveModal.records.length === 0 ? (
                <div className="text-center text-gray-500 py-10" data-testid="published-archive-empty-state">
                  Aucun planning publié archivé pour cette semaine.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border" data-testid="published-archive-table">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left border">Salarié (figé)</th>
                        <th className="p-2 text-left border">Planning figé</th>
                        <th className="p-2 text-left border">Publié le</th>
                        <th className="p-2 text-left border">Hash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archiveModal.records.map((record) => (
                        <tr key={record.archive_id} className="border-t">
                          <td className="p-2 border font-medium">{record.employee_name || 'Inconnu'}</td>
                          <td className="p-2 border text-xs">{formatArchivePlanning(record.planning)}</td>
                          <td className="p-2 border text-xs">{record.published_at ? new Date(record.published_at).toLocaleString('fr-FR') : '-'}</td>
                          <td className="p-2 border text-[11px] font-mono">{record.snapshot_hash}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Deactivation Confirmation Modal */}
      {deactivateModal.open && deactivateModal.employee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Désactiver ce salarié ?</h3>
                  <p className="text-gray-500 text-sm">Cette action est réversible depuis le profil</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="font-medium text-gray-900">{deactivateModal.employee.name}</p>
                {deactivateModal.employee.email && (
                  <p className="text-sm text-gray-500">{deactivateModal.employee.email}</p>
                )}
                {deactivateModal.employee.contract_hours && (
                  <p className="text-sm text-blue-600">Contrat: {deactivateModal.employee.contract_hours}h/sem</p>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Le salarié sera retiré du planning et ne pourra plus se connecter jusqu&apos;à sa réactivation.
              </p>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-lg">
              <Button
                variant="outline"
                onClick={() => setDeactivateModal({ open: false, employee: null })}
                disabled={deactivating}
              >
                Annuler
              </Button>
              <Button
                onClick={handleQuickDeactivate}
                disabled={deactivating}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-deactivate-btn"
              >
                {deactivating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Désactivation...
                  </>
                ) : (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Désactiver
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminPlanning;
